import React, { useState, useEffect } from 'react';
import {
    Key,
    Save,
    Loader2,
    AlertCircle,
    CheckCircle,
    Eye,
    EyeOff,
    Settings,
    Info
} from 'lucide-react';
import { AuthService } from '../../../../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

interface CompassSettingsProps {
    onBack: () => void;
}

export const CompassSettings: React.FC<CompassSettingsProps> = ({ onBack }) => {
    const [apiKey, setApiKey] = useState('');
    const [existingKeyMasked, setExistingKeyMasked] = useState<string | null>(null);
    const [showKey, setShowKey] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Load current key status
    useEffect(() => {
        loadKeyStatus();
    }, []);

    const loadKeyStatus = async () => {
        try {
            setLoading(true);
            const response = await AuthService.authenticatedGet(
                `${API_BASE_URL}/claude-query/settings/api-key-status`
            );
            if (response.ok) {
                const data = await response.json();
                setExistingKeyMasked(data.maskedKey || null);
            }
        } catch (err) {
            // Endpoint might not exist yet - that's OK
            console.log('Could not load API key status');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!apiKey.trim()) {
            setStatus({ type: 'error', message: 'API-nøkkel kan ikke være tom' });
            return;
        }

        if (!apiKey.startsWith('sk-ant-')) {
            setStatus({ type: 'error', message: 'Ugyldig format. Anthropic API-nøkler begynner med sk-ant-' });
            return;
        }

        try {
            setSaving(true);
            setStatus(null);

            const response = await AuthService.authenticatedPost(
                `${API_BASE_URL}/claude-query/settings/api-key`,
                { apiKey: apiKey.trim() }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Kunne ikke lagre API-nøkkel');
            }

            setStatus({ type: 'success', message: 'API-nøkkel lagret!' });
            setApiKey('');
            await loadKeyStatus(); // Refresh masked key
        } catch (err: any) {
            setStatus({ type: 'error', message: err.message || 'Feil ved lagring' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-white rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center gap-3">
                    <Settings size={20} className="text-gray-500" />
                    <h2 className="text-lg font-bold text-gray-800">Compass Innstillinger</h2>
                </div>
                <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded hover:bg-gray-100"
                >
                    Tilbake
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
                {/* API Key Section */}
                <div className="border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <Key size={18} className="text-blue-500" />
                        <h3 className="text-sm font-semibold text-gray-700">Claude API-nøkkel</h3>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                            <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-blue-700 space-y-1">
                                <p>Compass bruker Claude AI for å besvare spørsmål om lagerdataene.</p>
                                <p>Du kan sette en egen API-nøkkel for dette firmaet, eller bruke systemets standardnøkkel.</p>
                                <p>API-nøkler finner du på <span className="font-medium">console.anthropic.com</span></p>
                            </div>
                        </div>
                    </div>

                    {/* Current key status */}
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                            <Loader2 size={14} className="animate-spin" />
                            Laster...
                        </div>
                    ) : existingKeyMasked ? (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <CheckCircle size={14} className="text-green-500" />
                            <span className="text-sm text-green-700">
                                Nåværende nøkkel: <code className="bg-green-100 px-1.5 py-0.5 rounded text-xs">{existingKeyMasked}</code>
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <Info size={14} className="text-gray-400" />
                            <span className="text-sm text-gray-600">
                                Ingen firmaspesifikk nøkkel satt. Bruker systemets standardnøkkel.
                            </span>
                        </div>
                    )}

                    {/* Input */}
                    <div className="space-y-3">
                        <label className="block text-xs font-medium text-gray-600">
                            {existingKeyMasked ? 'Oppdater API-nøkkel' : 'Sett API-nøkkel'}
                        </label>
                        <div className="relative">
                            <input
                                type={showKey ? 'text' : 'password'}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="sk-ant-api03-..."
                                className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg text-sm font-mono
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                            <button
                                type="button"
                                onClick={() => setShowKey(!showKey)}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>

                        {/* Status message */}
                        {status && (
                            <div className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${
                                status.type === 'success'
                                    ? 'bg-green-50 border border-green-200 text-green-700'
                                    : 'bg-red-50 border border-red-200 text-red-700'
                            }`}>
                                {status.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                                {status.message}
                            </div>
                        )}

                        <button
                            onClick={handleSave}
                            disabled={saving || !apiKey.trim()}
                            className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-md text-sm
                                     hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            {saving ? 'Lagrer...' : 'Lagre nøkkel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
